import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  fetchImpersonationSession,
  startImpersonationSession,
  stopImpersonationSession,
} from "../api.js";

const ImpersonationContext = createContext({
  loading: true,
  impersonating: false,
  session: null,
  error: null,
  updating: false,
  refresh: async () => {},
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
});

export function ImpersonationProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    impersonating: false,
    session: null,
    error: null,
    updating: false,
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchImpersonationSession();
      const impersonating = Boolean(data?.impersonating);
      setState({
        loading: false,
        impersonating,
        session: impersonating ? data.session || null : null,
        error: null,
        updating: false,
      });
      return data;
    } catch (err) {
      setState({ loading: false, impersonating: false, session: null, error: err, updating: false });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const startImpersonation = useCallback(async (tenantId, userId) => {
    setState((prev) => ({ ...prev, updating: true, error: null }));
    try {
      const result = await startImpersonationSession(tenantId, userId);
      const impersonating = Boolean(result?.impersonating ?? true);
      const session = result?.session || null;
      setState({ loading: false, impersonating, session, error: null, updating: false });
      return session;
    } catch (err) {
      setState((prev) => ({ ...prev, updating: false, error: err }));
      throw err;
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    setState((prev) => ({ ...prev, updating: true, error: null }));
    try {
      await stopImpersonationSession();
      setState({ loading: false, impersonating: false, session: null, error: null, updating: false });
    } catch (err) {
      setState((prev) => ({ ...prev, updating: false, error: err }));
      throw err;
    }
  }, []);

  const value = useMemo(
    () => ({
      loading: state.loading,
      impersonating: state.impersonating,
      session: state.session,
      error: state.error,
      updating: state.updating,
      refresh,
      startImpersonation,
      stopImpersonation,
    }),
    [state, refresh, startImpersonation, stopImpersonation]
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
